import { useCallback, useEffect, useRef } from 'react';

import qs from 'qs';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useCancelableNavigate } from '#src/app/shared/hooks/navigate';
import type { ShareChannel } from '#src/app/shared/hooks/share';
import { canRedirectAfterShare } from '#src/app/shared/share/webviewRedirect';

import { usePromoteOrShareActionUrls } from '../../../usePromoteOrShareActionUrls';
import type { ShareFileMethod } from '../../../useShareFile';

const COPY_REDIRECT_DELAY = 2000;

// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
/** The QR code experiment's file shares, as `slide_up_source`: Save on the QR view, and Print on the poster view. */
export type QrShareSource = 'qr_code' | 'qr_poster';

const generateRedirectUrl = (shareButtonUrl: string, source: ShareChannel | QrShareSource) => {
	const [baseUrl, queryString] = shareButtonUrl.split('?');

	const queryParams = queryString ? Object.fromEntries(new URLSearchParams(queryString)) : {};

	return `${baseUrl}${qs.stringify(
		{
			...queryParams,
			slide_up_source: source,
		},
		{ addQueryPrefix: true },
	)}`;
};

type UseRedirectResult = {
	redirect: (channel: ShareChannel) => void;
	/**
	 * Supersedes the copy-link redirect when a later channel tap should keep the user here — wired to
	 * `onBeforeShare` in `../options.ts`, which documents why (CHANGE-97962).
	 *
	 * Clearing the armed timer is not enough on its own: `redirect('copy')` isn't reached until
	 * `./index.ts` finishes awaiting `createNewShare`, a real mutation that runs whenever the signer
	 * kept their signature public (the sign form's default). A tap landing during that round trip
	 * would find nothing armed yet, and the copy continuation would then arm a fresh timer. So taps
	 * are also counted, and a copy continuation only arms if its own tap is still the latest one.
	 *
	 * Must stay referentially stable: it ends up in `ShareActionsOptions`, and an identity change
	 * there rebuilds every channel's `ShareAction` component (see the `shareCopyOverride` note in
	 * `useShareActions/index.ts`).
	 */
	onShareTapped: (channel: ShareChannel) => void;
	// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
	/**
	 * Redirects after a QR Save or Print. A file share never navigates, so webviews redirect too. A share
	 * sheet has already finished, so the redirect runs at once (deferred while the page is hidden). A
	 * download gives no signal that it finished, so it waits as long as copy does, and a channel tap in
	 * that time supersedes it.
	 */
	redirectAfterQrShare: (source: QrShareSource, method: ShareFileMethod) => void;
	// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
	/**
	 * Cancels a pending download or copy-link redirect, so it does not navigate away while the supporter
	 * is still in the QR flow. It counts as a tap, like `onShareTapped`, so a copy share still awaiting
	 * `createNewShare` does not arm its timer afterward.
	 */
	cancelQrRedirect: () => void;
};

export function useRedirect(): UseRedirectResult {
	const {
		data: { shareButtonUrl },
	} = usePromoteOrShareActionUrls();

	const {
		userAgent: { isWebview },
	} = useUtilityContext();

	const { cancelableNavigate } = useCancelableNavigate();

	const timeoutRef = useRef<number | undefined>(undefined);
	// Both start at 0 so a `redirect('copy')` that never saw a tap (any consumer not wiring
	// `onBeforeShare`) still arms, rather than being silently superseded by a tap that never happened.
	const tapCountRef = useRef(0);
	const copyTapRef = useRef(0);

	const clearPendingRedirect = useCallback(() => {
		window.clearTimeout(timeoutRef.current);
		timeoutRef.current = undefined;
	}, []);

	const onShareTapped = useCallback(
		(channel: ShareChannel) => {
			clearPendingRedirect();
			tapCountRef.current += 1;
			// Copy records the tap it just created, so its own tap never supersedes its own redirect.
			if (channel === 'copy') {
				copyTapRef.current = tapCountRef.current;
			}
		},
		[clearPendingRedirect],
	);

	useEffect(() => clearPendingRedirect, [clearPendingRedirect]);

	return {
		redirect: useCallback(
			(channel: ShareChannel) => {
				if (!canRedirectAfterShare({ channel, isWebview })) return;

				const redirectUrl = generateRedirectUrl(shareButtonUrl, channel);

				if (channel === 'copy') {
					// A later tap happened while this copy share was still in flight — it owns the page now.
					if (copyTapRef.current !== tapCountRef.current) return;

					clearPendingRedirect();
					timeoutRef.current = window.setTimeout(() => {
						timeoutRef.current = undefined;
						cancelableNavigate({
							eventName: 'shareChannelSelectedRedirect',
							eventConfig: {
								selectedChannel: channel,
							},
							redirectUrl,
							deferWhileHidden: true,
						});
					}, COPY_REDIRECT_DELAY);
					return;
				}

				cancelableNavigate({
					eventName: 'shareChannelSelectedRedirect',
					eventConfig: {
						selectedChannel: channel,
					},
					redirectUrl,
					deferWhileHidden: true,
				});
			},
			[isWebview, cancelableNavigate, shareButtonUrl, clearPendingRedirect],
		),
		onShareTapped,
		redirectAfterQrShare: useCallback(
			(source: QrShareSource, method: ShareFileMethod) => {
				const navigateToSharePage = () =>
					cancelableNavigate({
						eventName: 'shareChannelSelectedRedirect',
						eventConfig: {
							selectedChannel: source,
						},
						redirectUrl: generateRedirectUrl(shareButtonUrl, source),
						deferWhileHidden: true,
					});

				clearPendingRedirect();
				if (method === 'share_sheet') {
					navigateToSharePage();
					return;
				}
				timeoutRef.current = window.setTimeout(() => {
					timeoutRef.current = undefined;
					navigateToSharePage();
				}, COPY_REDIRECT_DELAY);
			},
			[cancelableNavigate, shareButtonUrl, clearPendingRedirect],
		),
		cancelQrRedirect: useCallback(() => {
			clearPendingRedirect();
			tapCountRef.current += 1;
		}, [clearPendingRedirect]),
	};
}
